import React from "react";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { DATE_FORMAT } from "@/constants/datetime";
import { MessageBox } from "@/features/chat/components/MessageBox";
import { useChat } from "@/features/chat/providers/ChatProvider";
import { useDialog } from "@/hooks/useDialog";
import { useMount } from "@/hooks/useMount";
import { text } from "@/theme/colors";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { Box, CircularProgress, styled, Typography } from "@mui/material";
import { useEffect, useState } from "react";

export const Room = () => {
  const { currentRoom } = useChat();
  const isMounted = useMount();

  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const confirmModal = useDialog();

  const handleScroll = async () => {
    if (!currentRoom.roomRef.current || isFetchingMore || !currentRoom.hasNextPage) return;

    if (currentRoom.roomRef.current?.scrollTop === 0) {
      setIsFetchingMore(true);
      await currentRoom.fetchNextPage();
      setTimeout(() => {
        setIsFetchingMore(false);
      }, 200);
    }
  };

  useEffect(() => {
    if (!isMounted) return;

    const scrollContainer = currentRoom.roomRef.current;
    scrollContainer?.addEventListener("scroll", handleScroll);
    return () => {
      scrollContainer?.removeEventListener("scroll", handleScroll);
    };
  }, [isMounted, currentRoom, isFetchingMore]);

  const handleRemoveMessage = async (id: number) => {
    try {
      const agree = await confirmModal.show();
      if (!agree) return;
      await currentRoom?.deleteMsg(id);
    } catch (error) {
      showError(error);
    }
  };

  const isExistMessageList = [...currentRoom.oldMessages, ...currentRoom.newMessages].length > 0;

  // Merge old and new message, remove duplicate, only when not fetching
  const mergedMessages = currentRoom.isFetching
    ? currentRoom.oldMessages
    : [...currentRoom.oldMessages, ...currentRoom.newMessages.filter((newMsg) => !currentRoom.oldMessages.some((oldMsg) => oldMsg.id === newMsg.id))];

  // Scroll to bottom when messages change
  useEffect(() => {
    if (!isMounted || !isExistMessageList || currentRoom.isFetching) return;
    currentRoom.scrollToBottom();
  }, [isMounted, isExistMessageList, currentRoom.isFetching, mergedMessages]);

  return (
    <>
      <RoomWrapper ref={currentRoom.roomRef}>
        {(currentRoom.isFetching || isFetchingMore) && (
          <SpinnerContainer>
            <CircularProgress color={"primary"} />
          </SpinnerContainer>
        )}
        {!currentRoom.isFetching &&
          mergedMessages.map((item, index, arr) => {
            const currentDate = item.createdAt ? dayjs(item.createdAt).format(DATE_FORMAT) : null;
            const prevDate = index > 0 && arr[index - 1].createdAt ? dayjs(arr[index - 1].createdAt).format(DATE_FORMAT) : null;

            return (
              <React.Fragment key={item.id}>
                {currentDate && currentDate !== prevDate && (
                  <Typography component="p" my={2} textAlign="center" variant="sub16Semi" color={text.secondary}>
                    {currentDate}
                  </Typography>
                )}
                <MessageBox {...item} handleRemoveMessage={handleRemoveMessage} />
              </React.Fragment>
            );
          })}
      </RoomWrapper>

      <ConfirmSaveModal open={confirmModal.open} onOk={confirmModal.confirm} onClose={confirmModal.cancel}>
        <Typography>削除すると元に戻せません。削除してもよろしいですか？</Typography>
      </ConfirmSaveModal>
    </>
  );
};

const RoomWrapper = styled(Box)`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  padding-left: 8px;
  padding-right: 8px;
  overflow: auto;
  background-color: white;
  flex: 1 1 auto;
  flex-shrink: 1;
`;

const SpinnerContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
`;
